import React from 'react';
import { Alert, AlertTitle, Box, Chip, Typography } from '@mui/material';
import { CheckCircle, WarningAmber } from '@mui/icons-material';

/**
 * ShortEligibilityWarning — pre-submit eligibility check for stock bots.
 *
 * Replaces PdtSubmitWarning (the PDT rule was retired June 4, 2026; UIs must
 * not surface PDT status, day-trade counts, or DTBP). What matters for a
 * stock bot at submit time now is whether the account can actually take the
 * trades the bot is configured to make — above all, shorts.
 *
 * Renders on the final create-bot step:
 *   <ShortEligibilityWarning
 *     assetClass={assetClass}                      // from useAssetClass
 *     allowShorts={newBot.allowShorts}
 *     accountStatus={accountStatusForSelected}     // alpacaStatus.accounts[exchangeId]
 *   />
 *
 * Expected accountStatus shape (from GET /api/alpaca/account-status, which
 * reads the account-poller snapshot in Redis — see alpacaAccountStatusRoute.js):
 *   {
 *     accountType: 'cash' | 'margin',
 *     shortingEnabled: boolean,
 *     equity: number,
 *     buyingPower: number,
 *     cash: number,
 *     status: 'ACTIVE' | ...,
 *     tradingBlocked: boolean,
 *     accountBlocked: boolean,
 *     authError: boolean,
 *     polledAt: number (epoch ms),
 *   }
 *
 * Behavior matrix (first match wins):
 *   not stock_equity                      → render nothing
 *   no accountStatus (no exchange/data)   → info: can't verify, checked at submission
 *   authError                             → error: reconnect API keys
 *   tradingBlocked / accountBlocked /     → error: account can't trade (applies to
 *     status !== ACTIVE                     long-only bots too, so shown regardless
 *                                           of allowShorts)
 *   --- below only when allowShorts ---
 *   accountType === 'cash'                → error: cash accounts can't short
 *   shortingEnabled === false             → error: shorting disabled on account
 *   equity < $2,000                       → warning: below Reg-T margin minimum
 *   all pass                              → compact success line
 *
 * Errors here are advisory — the Save buttons stay enabled because the
 * backend account guard independently rejects ineligible SHORT_OPENs. This
 * component exists so the user learns at creation time, not from a rejected
 * order at 9:31 the next morning.
 */

const SNAPSHOT_STALE_MS = 5 * 60 * 1000;

const fmtUsd = (v) =>
  Number.isFinite(Number(v))
    ? `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : '—';

export function ShortEligibilityWarning({ assetClass, allowShorts, accountStatus }) {
  if (assetClass !== 'stock_equity') return null;

  const sx = { mt: 3 };

  // ── No snapshot: exchange not selected yet, poller not running, or the
  //    status endpoint hasn't returned. Only worth saying when shorts are on.
  if (!accountStatus) {
    if (!allowShorts) return null;
    return (
      <Alert severity="info" sx={sx}>
        <AlertTitle>Short eligibility not verified</AlertTitle>
        <Typography variant="body2">
          Account status isn't available right now, so short eligibility
          (margin account, shorting enabled, $2,000 minimum equity) couldn't be
          checked. The bot will verify each short order at submission and
          reject it if the account doesn't qualify.
        </Typography>
      </Alert>
    );
  }

  const {
    accountType,
    shortingEnabled,
    equity,
    status,
    tradingBlocked,
    accountBlocked,
    authError,
    polledAt,
  } = accountStatus;

  const equityNum = Number(equity);
  const stale =
    Number.isFinite(Number(polledAt)) && Date.now() - Number(polledAt) > SNAPSHOT_STALE_MS;

  const staleNote = stale ? (
    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
      Account data last updated {new Date(Number(polledAt)).toLocaleTimeString()} — it may be
      out of date.
    </Typography>
  ) : null;

  // ── Account-level blockers: relevant even for long-only stock bots.
  if (authError) {
    return (
      <Alert severity="error" sx={sx}>
        <AlertTitle>Alpaca connection failed</AlertTitle>
        <Typography variant="body2">
          The API keys for this exchange were rejected by Alpaca. The bot can't
          place any orders until the keys are updated — open the exchange in
          Portfolio and use Manage Keys to reconnect.
        </Typography>
      </Alert>
    );
  }

  if (tradingBlocked || accountBlocked || (status && status !== 'ACTIVE')) {
    return (
      <Alert severity="error" sx={sx}>
        <AlertTitle>Account can't trade right now</AlertTitle>
        <Typography variant="body2">
          {accountBlocked
            ? 'Alpaca reports this account as blocked.'
            : tradingBlocked
              ? 'Alpaca reports trading as blocked on this account.'
              : `Account status is ${status}.`}{' '}
          The bot will be created but every order will be rejected until the
          restriction is lifted. Check your Alpaca dashboard for details.
        </Typography>
        {staleNote}
      </Alert>
    );
  }

  // ── Short-specific checks.
  if (!allowShorts) return null;

  if (accountType === 'cash') {
    return (
      <Alert severity="error" sx={sx}>
        <AlertTitle>Cash account — shorts will be rejected</AlertTitle>
        <Typography variant="body2">
          This bot has Allow Shorts on, but the connected Alpaca account is a
          cash account. Short selling requires a margin account, so every
          short-open signal will be rejected; long trades are unaffected. To
          short, upgrade the account to margin in your Alpaca dashboard, or
          turn Allow Shorts off.
        </Typography>
        {staleNote}
      </Alert>
    );
  }

  if (shortingEnabled === false) {
    return (
      <Alert severity="error" sx={sx}>
        <AlertTitle>Shorting is disabled on this account</AlertTitle>
        <Typography variant="body2">
          The account is margin-enabled but Alpaca reports shorting as turned
          off, so short-open signals will be rejected. Enable shorting in your
          Alpaca account settings, or turn Allow Shorts off.
        </Typography>
        {staleNote}
      </Alert>
    );
  }

  if (Number.isFinite(equityNum) && equityNum < 2000) {
    return (
      <Alert severity="warning" sx={sx}>
        <AlertTitle>Equity below the $2,000 margin minimum</AlertTitle>
        <Typography variant="body2">
          Account equity is {fmtUsd(equityNum)}. Short positions require at
          least $2,000 of equity (Reg-T margin minimum), so short-open signals
          will be rejected until the account is funded above that level. Long
          trades are unaffected.
        </Typography>
        {staleNote}
      </Alert>
    );
  }

  // ── All checks pass: one quiet confirmation line.
  return (
    <Box
      sx={{
        mt: 3,
        px: 1.5,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: '#065f46',
        bgcolor: '#052e16',
      }}
    >
      <CheckCircle sx={{ fontSize: 18, color: '#46b88a' }} />
      <Typography variant="body2" sx={{ color: '#a7f3d0' }}>
        Account is short-eligible — margin account, shorting enabled
        {Number.isFinite(equityNum) ? `, equity ${fmtUsd(equityNum)}` : ''}.
      </Typography>
      {stale && (
        <Chip
          size="small"
          icon={<WarningAmber sx={{ fontSize: 14 }} />}
          label="data may be stale"
          variant="outlined"
          sx={{ ml: 'auto', height: 22, color: '#d9a441', borderColor: '#78350f' }}
        />
      )}
    </Box>
  );
}

export default ShortEligibilityWarning;